import { useCallback, useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { callAction } from '@/api/client'
import { actionKey, useActionQuery, useAvailability } from '@/api/hooks'
import type { Out } from '@/api/generated/types'
import { createStore } from '@/lib/store'

export const TourA = {
  list: 'prefs.tours',
  set: 'prefs.tour_set',
  reset: 'prefs.tours_reset',
} as const

export type TourStatus = 'in_progress' | 'completed' | 'skipped'
export type TourState = NonNullable<Out<'prefs.tours'>>[number]

/* ------------------------------------------------------------ running tour */

export interface ActiveTour {
  id: string
  step: number
  /** Direction of the last move: a missing anchor is skipped in this direction. */
  dir: 1 | -1
}

export const tourStore = createStore<{ active?: ActiveTour }>({})

export function useActiveTour(): ActiveTour | undefined {
  return useSyncExternalStore(tourStore.subscribe, () => tourStore.get().active, () => tourStore.get().active)
}

/** Start (or resume at `step`) a tour. */
export function startTour(id: string, step = 0) {
  tourStore.set({ active: { id, step: Math.max(0, step), dir: 1 } })
}

export function endTour() {
  tourStore.set({ active: undefined })
}

/* ------------------------------------------------------------- persistence */

const LOCAL_KEY = 'dv.tours'

/** Local mirror: keeps tours usable when the server action is not deployed (and in dev). */
export function readLocalTours(): Record<string, TourState> {
  try {
    const v = JSON.parse(localStorage.getItem(LOCAL_KEY) ?? '{}') as unknown
    return v && typeof v === 'object' ? (v as Record<string, TourState>) : {}
  } catch {
    return {}
  }
}

function writeLocalTour(s: TourState) {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify({ ...readLocalTours(), [s.tour_id]: s }))
  } catch {
    /* storage unavailable */
  }
}

/** Merge a saved state into a cached `prefs.tours` list. */
export function upsertTourState(list: TourState[] | undefined, s: TourState): TourState[] {
  const rest = (list ?? []).filter((x) => x.tour_id !== s.tour_id)
  return [...rest, s].sort((a, b) => a.tour_id.localeCompare(b.tour_id))
}

export interface TourStates {
  /** State per tour id. */
  byId: Record<string, TourState>
  /** The list has been read (from the server when available, else from the local mirror). */
  loaded: boolean
  /** Progress is stored on the server (per user). */
  server: boolean
}

/** The current user's tour progress: server-side per user, falling back to the local mirror. */
export function useTourStates(): TourStates {
  const q = useActionQuery<TourState[]>(TourA.list, {}, { staleTime: 5 * 60_000 })
  if (q.availability === 'ok') {
    const byId: Record<string, TourState> = {}
    for (const s of q.data ?? []) byId[s.tour_id] = s
    return { byId, loaded: q.isSuccess, server: true }
  }
  return { byId: readLocalTours(), loaded: q.availability !== 'loading', server: false }
}

/** Save progress: optimistic cache update, local mirror, then the server (errors are non-fatal). */
export function useSaveTour() {
  const qc = useQueryClient()
  const avail = useAvailability(TourA.set)
  return useCallback(
    async (id: string, status: TourStatus, step = 0) => {
      const now = new Date().toISOString()
      const s: TourState = { tour_id: id, status, step, updated_at: now, ...(status === 'completed' ? { completed_at: now } : {}) }
      writeLocalTour(s)
      qc.setQueryData<TourState[]>(actionKey(TourA.list, {}), (old) => upsertTourState(old, s))
      if (avail !== 'ok') return
      try {
        await callAction(TourA.set, { tour_id: id, status, step })
      } catch {
        /* progress is a convenience; never break the page over it */
      }
    },
    [qc, avail],
  )
}
